import IHttpClientDeleteParameters from '@/modules/common/contracts/http-client/http-client-delete-parameters.interface'
import IHttpClientGetParameters from '@/modules/common/contracts/http-client/http-client-get-parameters.interface'
import IHttpClientPatchParameters from '@/modules/common/contracts/http-client/http-client-patch-parameters.interface'
import IHttpClientPostParameters from '@/modules/common/contracts/http-client/http-client-post-parameters.interface'
import HttpClient from '@/modules/common/services/http-client.service'
import Container, { Service } from 'typedi'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ContentApiOptions } from '@/interfaces'
import { CEU } from '@/modules/common/models'

@Service()
export default class LearningService {
  public async fetchGroups (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/learning/people/groups`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async createGroup (company_uuid: string, data: any): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/learning/people/group`,
      requireToken: true,
      payload: data
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async updateGroup (company_uuid: string, lms_group_uuid: string, data: any): Promise<AxiosResponse> {
    const httpParams: IHttpClientPatchParameters = {
      url: `/company/${company_uuid}/learning/people/group/${lms_group_uuid}`,
      requireToken: true,
      payload: data
    }
    return await Container.get(HttpClient).patch(httpParams)
  }

  public async getGroup (company_uuid: string, lms_group_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientDeleteParameters = {
      url: `/company/${company_uuid}/learning/people/group/${lms_group_uuid}`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async syncLmsGroups (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/learning/people/groups/sync`,
      requireToken: true
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async assignCourseToGroup (company_uuid: string, course_uuid: string, lms_group_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/group/${lms_group_uuid}`,
      requireToken: true
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async getGroupForCourse (company_uuid: string, course_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/group`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async removeCourseFromGroup (company_uuid: string, course_uuid: string, lms_group_uuid): Promise<AxiosResponse> {
    const httpParams: IHttpClientDeleteParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/group/${lms_group_uuid}`,
      requireToken: true
    }
    return await Container.get(HttpClient).delete(httpParams)
  }

  public async getContentByType (event_uuid: string, type: string, options?: ContentApiOptions): Promise<AxiosResponse> {
    const { env, no_data } = options || {}
    const queryParams: string[] = []
    if (no_data) {
      queryParams.push('no_data=true')
    }
    let url = `/event/${event_uuid}/content/${type}`
    if (queryParams.length) {
      url += `?${queryParams.join('&')}`
    }
    const httpParams: IHttpClientGetParameters = {
      url,
      requireToken: true
    }
    if (env) {
      httpParams.config = {
        headers: {
          environment: env
        }
      } as AxiosRequestConfig
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async getContentByTypeAndSubtype (event_uuid: string, type: string, sub_type: string) {
    const httpParams: IHttpClientGetParameters = {
      url: `/event/${event_uuid}/content/${type}/${sub_type}`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async getContentByTypeAndSubtypeNoData (event_uuid: string, type: string, sub_type: string) {
    const httpParams: IHttpClientGetParameters = {
      url: `/event/${event_uuid}/content/${type}/${sub_type}/all?no_data=true`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async getContentVersionsByTypeAndSubtype (event_uuid: string, type: string, sub_type: string) {
    const httpParams: IHttpClientGetParameters = {
      url: `/event/${event_uuid}/content/versions/${type}/${sub_type}`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async getChapterContents (event_uuid: string, chapter_uuid: string, env?: string, no_data?: boolean) {
    const httpParams: IHttpClientGetParameters = {
      url: `/event/${event_uuid}/chapter/${chapter_uuid}/contents` + (no_data ? '?no_data=true' : ''),
      requireToken: true
    }
    if (env) {
      httpParams.config = {
        headers: {
          environment: env
        }
      } as AxiosRequestConfig
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async getContentByUUID (event_uuid: string, content_uuid: string) {
    const httpParams: IHttpClientGetParameters = {
      url: `/event/${event_uuid}/content/uuid/${content_uuid}`,
      requireToken: true
    }
    const response = await Container.get(HttpClient).get(httpParams)
    return response
  }

  public async getTableOfContents (event_uuid: string) {
    const httpParams: IHttpClientGetParameters = {
      url: `/event/${event_uuid}/course-contents`,
      requireToken: true,
      config: {
        headers: {
          environment: 'dev'
        }
      }
    }
    const response: any = await Container.get(HttpClient).get(httpParams)
    return response
  }

  public async saveContent (event_uuid: string, type: string, sub_type: string, data: any): Promise<any> {
    const httpParams: IHttpClientPostParameters = {
      url: `/event/${event_uuid}/content/${type}/${sub_type}`,
      requireToken: true,
      payload: data
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async bulkUpdateContentOrder (event_uuid: string, data: { content_order: string[] }): Promise<any> {
    const httpParams: IHttpClientPatchParameters = {
      url: `/event/${event_uuid}/content/order`,
      requireToken: true,
      payload: data
    }
    return await Container.get(HttpClient).patch(httpParams)
  }

  public async deleteContent (event_uuid: string, type: string, sub_type: string): Promise<any> {
    const httpParams: IHttpClientPostParameters = {
      url: `/event/${event_uuid}/content/${type}/${sub_type}`,
      requireToken: true
    }
    return await Container.get(HttpClient).delete(httpParams)
  }

  public async deleteAllContentOfType (event_uuid: string, type: string): Promise<any> {
    const httpParams: IHttpClientPostParameters = {
      url: `/event/${event_uuid}/content/${type}`,
      requireToken: true
    }
    return await Container.get(HttpClient).delete(httpParams)
  }

  public async fetchLmsCeus (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/learning/ceu`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async createOrEditLmsCeu (company_uuid: string, ceu: CEU): Promise<AxiosResponse> {
    const url = ceu.uuid ? `/company/${company_uuid}/learning/ceu/${ceu.uuid}` : `/company/${company_uuid}/learning/ceu`
    const httpParams: IHttpClientPostParameters | IHttpClientPatchParameters = {
      url,
      requireToken: true,
      payload: {
        ...ceu
      }
    }
    if (ceu.uuid) {
      return await Container.get(HttpClient).patch(httpParams)
    } else {
      return await Container.get(HttpClient).post(httpParams)
    }
  }

  public async deleteLmsCeu (company_uuid: string, ceu_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientDeleteParameters = {
      url: `/company/${company_uuid}/learning/ceu/${ceu_uuid}`,
      requireToken: true
    }
    return await Container.get(HttpClient).delete(httpParams)
  }

  public async fetchCertificates (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/learning/certificates`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async updateCertificate (company_uuid: string, certificate_uuid: string, data: any): Promise<AxiosResponse> {
    const httpParams: IHttpClientPatchParameters = {
      url: `/company/${company_uuid}/learning/certificates/${certificate_uuid}`,
      requireToken: true,
      payload: data
    }
    return await Container.get(HttpClient).patch(httpParams)
  }

  public async createCertificate (company_uuid: string, data: any): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/learning/certificates`,
      requireToken: true,
      payload: data
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async fetchLmsStatistics (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/learning/stats`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async refetchLmsStatistics (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/learning/stats/refresh`,
      requireToken: true
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async fetchLmsStatisticsStatus (company_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/learning/stats/refresh/status`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async addCEUPointsToCourse (company_uuid: string, course_uuid: string, payload: { ceu_uuid: string; points: number }): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/ceu`,
      requireToken: true,
      payload
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async editCEUPointsToCourse (company_uuid: string, course_uuid: string, point_uuid: string, payload: { ceu_uuid: string; points: number }): Promise<AxiosResponse> {
    const httpParams: IHttpClientPatchParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/ceu/${point_uuid}`,
      requireToken: true,
      payload
    }
    return await Container.get(HttpClient).patch(httpParams)
  }

  public async removeCEUPointsToCourse (company_uuid: string, course_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientDeleteParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/ceu`,
      requireToken: true
    }
    return await Container.get(HttpClient).delete(httpParams)
  }

  public async addCertificateToCourse (company_uuid: string, course_uuid: string, certificate_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/certificate/${certificate_uuid}`,
      requireToken: true
    }
    return await Container.get(HttpClient).post(httpParams)
  }

  public async getCertificateForCourse (company_uuid: string, course_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientGetParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/certificate`,
      requireToken: true
    }
    return await Container.get(HttpClient).get(httpParams)
  }

  public async removeCertificateFromCourse (company_uuid: string, course_uuid: string, certificate_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientDeleteParameters = {
      url: `/company/${company_uuid}/course/${course_uuid}/certificate/${certificate_uuid}`,
      requireToken: true
    }
    return await Container.get(HttpClient).delete(httpParams)
  }

  public async seedLearners (company_uuid: string, course_uuid: string): Promise<AxiosResponse> {
    const httpParams: IHttpClientPostParameters = {
      url: `/company/${company_uuid}/learning/people/seed`,
      payload: {
        course_uuid
      },
      requireToken: true
    }
    return await Container.get(HttpClient).post(httpParams)
  }
}
